import { faUser } from "@fortawesome/free-regular-svg-icons";
import { faSignOutAlt, faUserDoctor } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { selectLoginStatus, setLoginStatus } from "state/slices/loginSlice";
import { setLoginView } from "state/slices/loginViewSlice";
import { selectRole, clearRole } from "state/slices/roleSlice";
import { selectUsername } from "state/slices/userSlice";
import LOGO from "assets/images/EReplyLogo.png";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setShowCandidateForm } from "state/slices/registerCandidateSlice";
import "styles/css/header.css";
import { MenuItems } from "./MenuItems";

const Header = () => {
  const navigate = useNavigate();
  const [menu, setMenu] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const loginState = useSelector(selectLoginStatus);
  const roleState = useSelector(selectRole) || localStorage.getItem("role");
  const user = useSelector(selectUsername);
  const isLoginPage =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/sign-in" ||
    location.pathname === "/candidate-login" ||
    location.pathname === "/404";
  const isRegisterPage = location.pathname === "/register";

  useEffect(() => {
    setMenu(MenuItems);
  }, []);

  useEffect(() => {
    // Function to check screen size
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 992);
    };
    // Initial check
    checkScreenSize();

    // Add event listener to handle window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.clear();
    dispatch(setLoginStatus(false));
    dispatch(setLoginView("login"));
    dispatch(clearRole());

    if (isRegisterPage) {
      dispatch(setShowCandidateForm(false));
    }
    navigate("/");
  };

  const renderDropdownMenu = () => (
    <>
      {!loginState ? (
        <>
          <li>
            <span
              className="dropdown-item cursor-pointer"
              onClick={() => {
                dispatch(setLoginView("login"));
                navigate("/");
              }}
            >
              <FontAwesomeIcon icon={faUserDoctor} className="me-2" />
              Login
            </span>
          </li>
        </>
      ) : (
        <>
          <li>
            <span
              className="dropdown-item cursor-pointer"
              onClick={() => {
                handleChangePassword();
              }}
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faUser} className="me-2" /> Change Password
            </span>
          </li>
          <li>
            <span
              className="dropdown-item cursor-pointer"
              onClick={(e) => {
                handleLogout(e);
              }}
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="me-2" /> Logout
            </span>
          </li>
        </>
      )}
    </>
  );

  const clearCacheExceptToken = () => {
    const authToken = localStorage.getItem("token");
    localStorage.clear();
    if (authToken) {
      localStorage.setItem("token", authToken);
    }
  };

  const renderMenu = (menuItems) => (
    <ul className="navbar-nav mx-auto">
      {menuItems.map((item, index) => (
        <li
          key={index}
          className="nav-item dropdown position-static py-lg-2 me-lg-n1 me-xl-0"
        >
          {item.url ? (
            <>
              {item.roles.includes(roleState) && (
                <span
                  className="nav-link cursor-pointer"
                  onClick={() => navigate(item.url)}
                >
                  {item.moduleName}
                </span>
              )}
            </>
          ) : (
            <>
              {item.roles.includes(roleState) && (
                <>
                  <span
                    className="nav-link dropdown-toggle"
                    href="#"
                    role="button"
                    data-bs-toggle="dropdown"
                    data-bs-trigger="hover"
                    aria-expanded="false"
                  >
                    {item.moduleName}
                  </span>
                  {renderSubMenu(item?.subMenu)}
                </>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );

  const renderSubMenu = (subMenu) => (
    <div
      className="dropdown-menu rounded-4"
      style={{ left: "auto", backgroundColor: "#F5F7F8" }}
    >
      <div className="d-flex flex-column gap-2">
        {subMenu.map((item, index) =>
          item.roles.includes(roleState) && (
            <span
              key={item.url || index}
              className="nav-link animate-underline animate-target fw-normal text-truncate p-0 cursor-pointer"
              onClick={() => navigate(item.url)}
            >
              {item.moduleName}
            </span>
          )
        )}

      </div>
    </div>
  );

  const handleChangePassword = () => {
    dispatch(setLoginView("change-password"));
    navigate("change-password");
  };

  return (
    <>
      <header
        className="navbar navbar-expand-lg bg-body sticky-top shadow-sm"
        style={{
          padding: "0px",
          marginBottom: "0.5%",
        }}
      >
        <div className="container-fluid py-2 d-flex justify-content-between align-items-center">
          <span
            className="navbar-brand cursor-pointer"
            onClick={(e) => {
              clearCacheExceptToken();
            }}
          >
            <div className="d-flex align-items-center" style={{ height: "90px" }}>
              <img
                src={LOGO}
                alt="E-Reply"
                style={{
                  height: isSmallScreen ? "45px" : "70px",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </div>
          </span>

          {(isSmallScreen && !isLoginPage) && (
            <button
              type="button"
              className="navbar-toggler"
              data-bs-toggle="offcanvas"
              data-bs-target="#navbarOffcanvas"
              aria-controls="navbarOffcanvas"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          )}

          <div
            className="offcanvas offcanvas-end"
            id="navbarOffcanvas"
            tabIndex="-1"
            aria-labelledby="navbarOffcanvasLabel"
          >
            <div className="offcanvas-header">
              {!isLoginPage && (
                <ul className="navbar-nav d-flex flex-row align-items-center position-relative">
                  <li className="nav-item dropdown position-relative">
                    <span
                      className="nav-link dropdown-toggle cursor-pointer"
                      role="button"
                      data-bs-toggle="dropdown"
                      data-bs-auto-close="outside"
                      aria-expanded="false"
                      style={{ fontSize: "20px" }}
                    >
                      <FontAwesomeIcon icon={faUser} />
                    </span>
                    <ul
                      className="dropdown-menu position-absolute"
                      style={{
                        top: "100%",
                        left: "0",
                        zIndex: "1050",
                      }}
                    >
                      {renderDropdownMenu()}
                    </ul>
                  </li>
                </ul>
              )}

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
              ></button>
            </div>

            <div className="offcanvas-body d-flex justify-content-between">
              {loginState &&
                ("Admin" === roleState ||
                  "Police" === roleState ||
                  "Advocate" === roleState) && <>{renderMenu(menu)}</>}

              {!isSmallScreen && !isLoginPage && (
                <>
                  <ul className="navbar-nav ms-auto align-items-center">
                    <li className="nav-item dropdown">
                      <span
                        className="nav-link dropdown-toggle cursor-pointer"
                        role="button"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                        aria-expanded="false"
                        style={{ fontSize: "20px" }}
                      >
                        {loginState && (
                          <>
                            <b className="ms-1">{user}</b>
                          </>
                        )}
                        <FontAwesomeIcon icon={faUser} className="ms-3" />
                      </span>
                      <ul className="dropdown-menu dropdown-menu-end">
                        {renderDropdownMenu()}
                      </ul>
                    </li>
                  </ul>
                </>
              )}
              {location.pathname === "/sheet" && (
                <ul className="navbar-nav ms-auto align-items-center">
                  <li className="nav-item dropdown">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => {
                        navigate("/");
                      }}
                    >
                      Logout
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
